import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid2,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  CircularProgress,
} from "@mui/material";
import { CacheProvider } from "@emotion/react";
import { prefixer } from "stylis";
import rtlPlugin from "stylis-plugin-rtl";
import createCache from "@emotion/cache";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowForwardIcon from "@mui/icons-material/ArrowBackSharp";
import { Link } from "react-router-dom";
import AddScheduleModal from "./AddScheduleModal";
import DeleteScheduleModal from "./DeleteScheduleModal";
import axiosInstance from "../../../../api/axiosConfig";

const cacheRtl = createCache({
  key: "adapter-date-fns-jalali-demo",
  stylisPlugins: [prefixer, rtlPlugin],
});

export default function AddSchedulePage() {
  const [selectedCamp, setSelectedCamp] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false); // Add delete modal state
  const [scheduleToDelete, setScheduleToDelete] = useState(null); // Store schedule to delete
  const [camps, setCamps] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Fetch camps data
  useEffect(() => {
    const fetchCamps = async () => {
      try {
        const response = await axiosInstance.get(
          "/Training/api/Training/GetTrainingCamp",
        );
        const formattedCamps = response.data.map((camp) => ({
          id: camp.trainigCampId,
          title: `${camp.campTitle} از تاریخ ${camp.startDate} الی ${camp.endDate}`,
        }));
        setCamps(formattedCamps);
      } catch (err) {
        if (err.response.data.message) {
          setError(`خطا: ${err.response.data.message}`);
        } else {
          setError("خطا در دریافت لیست اردوها");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCamps();
  }, []);

  // Fetch schedules data
  const fetchSchedules = async (campId) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        `/Training/api/Training/GetTrainingSchedules?TrainingCampId=${campId}`,
      );
      const formattedSchedules = response.data.map((schedule) => ({
        id: schedule.id,
        programDate: schedule.programDate,
        programDescription: schedule.programDescription,
        programStartTime: schedule.programStartTime,
        programEndTime: schedule.programEndTime,
        totalSessions: schedule.totalSessions,
      }));
      setSchedules(formattedSchedules);
    } catch (err) {
      if (err.response.data.message) {
        setError(`خطا: ${err.response.data.message}`);
      } else {
        setError("خطا در دریافت برنامه‌های تمرینی");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCampChange = (campId) => {
    setSelectedCamp(campId);
    fetchSchedules(campId); // Fetch schedules for the selected camp
  };

  const handleDeleteSchedule = async (scheduleId) => {
    try {
      await axiosInstance.post(
        `https://api.dsfai.ir/api/Training/api/Training/DeleteTrainingSchedule?Id=${scheduleId}`,
      );
      setSchedules(schedules.filter((schedule) => schedule.id !== scheduleId)); // Remove the deleted schedule from the state
      setDeleteModalOpen(false); // Close the delete modal
    } catch (err) {
      if (err.response.data.message) {
        setError(`خطا: ${err.response.data.message}`);
      } else {
        setError("خطا در حذف برنامه تمرینی");
      }
      setDeleteModalOpen(false);
    }
  };

  const openDeleteModal = (schedule) => {
    setScheduleToDelete(schedule); // Store schedule to delete
    setDeleteModalOpen(true); // Open the delete confirmation modal
  };

  if (loading) {
    return (
      <CircularProgress sx={{ display: "block", margin: "0 auto", mt: 4 }} />
    );
  }

  return (
    <Box sx={{ direction: "rtl" }}>
      {/* Camps Dropdown */}
      <Box
        sx={{
          border: "1px solid #ccc",
          borderRadius: "8px",
          backgroundColor: "#fff",
          marginBottom: 3,
          padding: 2,
        }}
      >
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: 2 }}>
          لیست اردوها
        </Typography>
        <CacheProvider value={cacheRtl}>
          <div dir="rtl">
            <FormControl fullWidth variant="outlined">
              <InputLabel id="camp-select-label">انتخاب اردو</InputLabel>
              <Select
                labelId="camp-select-label"
                value={selectedCamp}
                onChange={(e) => handleCampChange(e.target.value)}
                label="انتخاب اردو"
                dir="rtl"
              >
                {camps.map((camp) => (
                  <MenuItem key={camp.id} value={camp.id} dir="rtl">
                    {camp.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </CacheProvider>
      </Box>

      {/* Training Days Table */}
      <Box
        sx={{
          border: "1px solid #ccc",
          borderRadius: "8px",
          backgroundColor: "#fff",
          padding: 2,
        }}
      >
        <Grid2 container justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            روز های تمرین
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setModalOpen(true)}
            disabled={selectedCamp === ""}
          >
            افزودن روز تمرین جدید
          </Button>
        </Grid2>

        <TableContainer component={Paper} sx={{ marginTop: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="right">تاریخ</TableCell>
                <TableCell align="right" sx={{ minWidth: 120 }}>
                  عنوان
                </TableCell>
                <TableCell align="right">ساعت شروع</TableCell>
                <TableCell align="right">ساعت پایان</TableCell>
                <TableCell align="right">تعداد تمرین روز</TableCell>
                <TableCell align="right">عملیات</TableCell>
                <TableCell align="right">تمرین</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {schedules.length > 0 ? (
                schedules.map((schedule) => (
                  <TableRow key={schedule.id}>
                    <TableCell align="right">{schedule.programDate}</TableCell>
                    <TableCell align="right">
                      {schedule.programDescription}
                    </TableCell>
                    <TableCell align="right">
                      {schedule.programStartTime}
                    </TableCell>
                    <TableCell align="right">
                      {schedule.programEndTime}
                    </TableCell>
                    <TableCell align="right">
                      {schedule.totalSessions}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton color="primary" size="small">
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => openDeleteModal(schedule)} // Open delete modal
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                    <TableCell align="right">
                      <Link
                        to={`/dashboard/add-schedule/${schedule.id}`}
                        state={{ campId: selectedCamp }}
                        style={{ textDecoration: "none" }}
                      >
                        <Button
                          variant="contained"
                          color="info"
                          size="small"
                          endIcon={<ArrowForwardIcon />}
                        >
                          جزئیات تمرین
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    از لیست اردوها یک مورد را انتخاب کنید
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Add Schedule Modal */}
      <AddScheduleModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        trainingCampId={selectedCamp}
        onSuccess={() => fetchSchedules(selectedCamp)} // Refresh schedules
      />

      {/* Delete Schedule Confirmation Modal */}
      <DeleteScheduleModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={() => handleDeleteSchedule(scheduleToDelete.id)} // Pass the selected schedule id to delete
        scheduleDate={scheduleToDelete ? scheduleToDelete.programDate : ""}
      />
    </Box>
  );
}
